import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { LiveSocketName } from '../constants/liveReconnect';

interface ConnectionState {
  roomName: string; // Current room/bot ID
  isConnected: boolean;
  isReconnecting: boolean; // Flag to indicate reconnection in progress (prevents redirect)
  connectionStatus: 'disconnected' | 'connecting' | 'connected' | 'error';
  error?: string;
  serverPort?: number;
  /** Live sockets that dropped and are retrying (drives the reconnect banner). */
  reconnectingSockets: LiveSocketName[];
}

const initialState: ConnectionState = {
  roomName: '',
  isConnected: false,
  isReconnecting: false,
  connectionStatus: 'disconnected',
  reconnectingSockets: [],
};

const connectionSlice = createSlice({
  name: 'connection',
  initialState,
  reducers: {
    setRoomName: (state, action: PayloadAction<string>) => {
      state.roomName = action.payload;
    },

    setConnectionStatus: (state, action: PayloadAction<'disconnected' | 'connecting' | 'connected' | 'error'>) => {
      state.connectionStatus = action.payload;
      state.isConnected = action.payload === 'connected';
      if (action.payload !== 'error') {
        state.error = undefined;
      }
    },

    setConnectionError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.connectionStatus = 'error';
      state.isConnected = false;
    },

    setReconnecting: (state, action: PayloadAction<boolean>) => {
      state.isReconnecting = action.payload;
    },

    setServerPort: (state, action: PayloadAction<number>) => {
      state.serverPort = action.payload;
    },

    setLiveSocketReconnecting: (
      state,
      action: PayloadAction<{ socket: LiveSocketName; isReconnecting: boolean }>
    ) => {
      const { socket, isReconnecting } = action.payload;
      const others = state.reconnectingSockets.filter((name) => name !== socket);
      state.reconnectingSockets = isReconnecting ? [...others, socket] : others;
    },

    resetConnection: (state) => {
      state.isConnected = false;
      state.isReconnecting = false;
      state.connectionStatus = 'disconnected';
      state.error = undefined;
    },
  },
});

export const {
  setRoomName,
  setConnectionStatus,
  setConnectionError,
  setReconnecting,
  setServerPort,
  setLiveSocketReconnecting,
  resetConnection,
} = connectionSlice.actions;

export default connectionSlice.reducer;